/**
 * graft/A7 v2 (Rapid QA): the AI Overview card rendered INSIDE the reserved
 * QA slot.
 *
 * It is an overview for certain kinds of queries — a question, a quantity
 * ask, a status set, a superlative, a dated ask (useRapidAnswer.ts
 * `wantsOverview`) — and never a second result list: there is no best-match
 * strip, no kind mix, no "Summarize" button. A lookup gets its results and
 * nothing else.
 *
 * States. One frame; only the content inside it changes (UX rule D4):
 *
 *   armed        the live input is overview-shaped, nothing requested yet:
 *                the mark, the "AI Overview" label, three quiet placeholder
 *                lines. No animation — nothing is loading yet.
 *   connecting   label + "Thinking…", the lines shimmer
 *   facts        label + "Reading N results…", the lines shimmer
 *   streaming    prose growing in place with a soft cursor
 *   done         prose (three-line clamp with "Show more") over a footer:
 *                the cited records as chips with their kind icon, and
 *                "Ask a follow-up" into the agent dock
 *   unavailable  reasons the dock can do better on (unanswerable, timeout,
 *                upstream error, ungrounded, dropped stream): one quiet
 *                line + "Ask the search agent". Every other reason renders
 *                NOTHING and the slot collapses — an overview with nothing
 *                to say does not leave a box behind.
 *   error        renders nothing; the results below are unaffected.
 *
 * Links. Record citations arrive as [Title](rec:rN) — the agent dock's link
 * contract — and render inline as links through the dock's parseRecLinks
 * splitter. A link resolves its handle through `sources` to the record's
 * entity link (the same navigation a result-row click performs); an unknown
 * handle degrades to the plain title. The footer repeats the cited records
 * as chips so a reader who skims the prose still has somewhere to go.
 *
 * Timing is not printed (Google does not either); it rides on data
 * attributes (data-total-ms on the card) for probes and the bench.
 */
import { useMemo } from "react";

import { cn } from "@btnet/utilities-core";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";

import "./AiSearchRapid.less";

import { getEntityMeta } from "./entityMeta";
import type { IRapidAnswerSource, IRapidAnswerState } from "./hooks/useRapidAnswer";
import { parseRecLinks } from "./SearchAgentDock";

/** Reasons where inviting the dock is honest: the question needs more than one shot. */
const ASK_AGENT_REASONS = new Set([
    "unanswerable",
    "timeout",
    "upstream_error",
    "ungrounded",
    "stream_ended",
]);

/** How many cited records the footer shows as chips. */
const MAX_SOURCE_CHIPS = 4;

/** What the slot owes for a given state: which frame, or none. */
export type RapidOutcome = "frame" | "card" | "note" | "gone";

export function rapidOutcome(rapid: IRapidAnswerState): RapidOutcome {
    switch (rapid.phase) {
        case "idle":
            return "frame";
        case "connecting":
        case "facts":
        case "streaming":
        case "done":
            return "card";
        case "unavailable":
            return rapid.reason !== null && ASK_AGENT_REASONS.has(rapid.reason) ? "note" : "gone";
        default:
            return "gone";
    }
}

export interface IRapidAnswerProps {
    rapid: IRapidAnswerState;
    /** The prose is long enough to clamp (parent decides from the threshold). */
    clamped?: boolean;
    /** The user expanded a clamped answer (parent owns the state). */
    expanded?: boolean;
    onToggleExpand?: () => void;
    /** Open a cited record — same navigation a result-row click performs. */
    onOpenSource?: (source: IRapidAnswerSource) => void;
    /** Hand the question to the agent dock (bt:openSearchAgent). */
    onAskAgent?: (query: string) => void;
}

/** The four-point sparkle that marks generated content. Inline: the icon set has none. */
function OverviewMark() {
    return (
        <span className="ai-search-overview-mark" aria-hidden="true">
            <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
                <path
                    d="M7.5 1.6a.5.5 0 0 1 .96 0l1.2 3.7a.5.5 0 0 0 .32.32l3.7 1.2a.5.5 0 0 1 0 .96l-3.7 1.2a.5.5 0 0 0-.32.32l-1.2 3.7a.5.5 0 0 1-.96 0l-1.2-3.7a.5.5 0 0 0-.32-.32l-3.7-1.2a.5.5 0 0 1 0-.96l3.7-1.2a.5.5 0 0 0 .32-.32z"
                    fill="currentColor"
                />
                <path
                    d="M12.9 10.4a.3.3 0 0 1 .57 0l.42 1.3a.3.3 0 0 0 .19.19l1.3.42a.3.3 0 0 1 0 .57l-1.3.42a.3.3 0 0 0-.19.19l-.42 1.3a.3.3 0 0 1-.57 0l-.42-1.3a.3.3 0 0 0-.19-.19l-1.3-.42a.3.3 0 0 1 0-.57l1.3-.42a.3.3 0 0 0 .19-.19z"
                    fill="currentColor"
                    opacity="0.65"
                />
            </svg>
        </span>
    );
}

function PlaceholderLines(props: { live: boolean }) {
    return (
        <div
            className={cn("ai-search-overview-lines", {
                "ai-search-overview-lines--live": props.live,
            })}
            data-testid={props.live ? "ai-search-rapid-skeleton" : "ai-search-rapid-armed"}
            aria-hidden="true"
        >
            <span className="ai-search-overview-line" style={{ width: "92%" }} />
            <span className="ai-search-overview-line" style={{ width: "76%" }} />
            <span className="ai-search-overview-line" style={{ width: "48%" }} />
        </div>
    );
}

function statusCopy(rapid: IRapidAnswerState): string | null {
    switch (rapid.phase) {
        case "connecting":
            return "Thinking…";
        case "facts": {
            const n = rapid.facts?.result_count ?? 0;
            return n > 0 ? `Reading ${n} result${n === 1 ? "" : "s"}…` : "Thinking…";
        }
        default:
            return null;
    }
}

function noteCopy(reason: string | null): string {
    return reason === "timeout"
        ? "This one needs a closer look."
        : "No quick overview for this one.";
}

export function RapidAnswer(props: IRapidAnswerProps) {
    const {
        rapid,
        clamped = false,
        expanded = false,
        onToggleExpand,
        onOpenSource,
        onAskAgent,
    } = props;
    const outcome = rapidOutcome(rapid);

    const sourcesByHandle = useMemo(() => {
        const m = new Map<string, IRapidAnswerSource>();
        rapid.sources.forEach((s) => m.set(s.handle, s));
        return m;
    }, [rapid.sources]);
    const segments = useMemo(() => parseRecLinks(rapid.text), [rapid.text]);

    if (outcome === "gone") return null;

    if (outcome === "note") {
        return (
            <div
                className="ai-search-overview ai-search-overview--note"
                data-testid="ai-search-rapid"
                data-phase={rapid.phase}
                data-outcome="note"
                data-reason={rapid.reason ?? undefined}
            >
                <div className="ai-search-overview-head">
                    <OverviewMark />
                    <span
                        className="ai-search-overview-note-text"
                        data-testid="ai-search-rapid-fallback"
                    >
                        {noteCopy(rapid.reason)}
                    </span>
                    {onAskAgent && (
                        <BTButton
                            type="tertiary"
                            className="ai-search-overview-action"
                            data-testid="ai-search-rapid-ask-agent"
                            onClick={() => onAskAgent(rapid.query)}
                        >
                            Ask the search agent
                        </BTButton>
                    )}
                </div>
            </div>
        );
    }

    const status = statusCopy(rapid);
    const armed = rapid.phase === "idle";
    const working = rapid.phase === "connecting" || rapid.phase === "facts";
    const streaming = rapid.phase === "streaming";
    const done = rapid.phase === "done";
    const showProse = (streaming || done) && rapid.text.length > 0;
    const sources = rapid.sources.slice(0, MAX_SOURCE_CHIPS);

    return (
        <div
            className={cn("ai-search-overview", `ai-search-overview--${rapid.phase}`)}
            data-testid="ai-search-rapid"
            data-phase={rapid.phase}
            data-outcome={outcome}
            data-total-ms={rapid.totalMs ?? undefined}
            data-cached={rapid.cacheHit ? "true" : undefined}
        >
            <div className="ai-search-overview-head">
                <OverviewMark />
                <span className="ai-search-overview-label">AI Overview</span>
                {status && (
                    <span
                        className="ai-search-overview-status"
                        data-testid="ai-search-rapid-status"
                    >
                        {status}
                    </span>
                )}
            </div>

            {(armed || working) && <PlaceholderLines live={working} />}

            {showProse && (
                <div
                    className={cn("ai-search-overview-text", {
                        "ai-search-overview-text--clamped": clamped && !expanded,
                        "ai-search-overview-text--streaming": streaming,
                    })}
                    data-testid="ai-search-rapid-text"
                    aria-live="polite"
                    aria-busy={streaming}
                >
                    {segments.map((seg, i) => {
                        if (seg.kind === "text") {
                            return <span key={i}>{seg.text}</span>;
                        }
                        const src = sourcesByHandle.get(seg.handle);
                        if (!src || !onOpenSource) {
                            return (
                                <span key={i} className="ai-search-overview-link--plain">
                                    {seg.label}
                                </span>
                            );
                        }
                        return (
                            <BTButton
                                key={i}
                                type="tertiary"
                                className="ai-search-overview-link"
                                data-testid="ai-search-rapid-chip"
                                onClick={() => onOpenSource(src)}
                            >
                                {seg.label}
                            </BTButton>
                        );
                    })}
                    {streaming && <span className="ai-search-overview-cursor" aria-hidden="true" />}
                    {done && rapid.truncated && (
                        <span className="ai-search-overview-truncated"> (cut short)</span>
                    )}
                </div>
            )}

            {done && (
                <div className="ai-search-overview-foot" data-testid="ai-search-rapid-foot">
                    <div
                        className="ai-search-overview-sources"
                        data-testid="ai-search-rapid-sources"
                        aria-label="Sources"
                    >
                        {sources.map((src) => {
                            const meta = getEntityMeta(src.entity);
                            return (
                                <BTButton
                                    key={src.handle}
                                    type="tertiary"
                                    className="ai-search-overview-source"
                                    data-testid="ai-search-rapid-source"
                                    data-entity={src.entity}
                                    onClick={() => onOpenSource?.(src)}
                                    disabled={!onOpenSource}
                                >
                                    <span
                                        className="ai-search-overview-source-icon"
                                        aria-hidden="true"
                                    >
                                        {meta.icon}
                                    </span>
                                    <span className="ai-search-overview-source-title">
                                        {src.title}
                                    </span>
                                    {src.job && (
                                        <span className="ai-search-overview-source-job">
                                            {src.job}
                                        </span>
                                    )}
                                </BTButton>
                            );
                        })}
                    </div>
                    <div className="ai-search-overview-actions">
                        {clamped && onToggleExpand && (
                            <BTButton
                                type="tertiary"
                                className="ai-search-overview-action"
                                data-testid="ai-search-qa-expand"
                                onClick={onToggleExpand}
                            >
                                {expanded ? "Show less" : "Show more"}
                            </BTButton>
                        )}
                        {onAskAgent && (
                            <BTButton
                                type="tertiary"
                                className="ai-search-overview-action"
                                data-testid="ai-search-rapid-followup"
                                onClick={() => onAskAgent(rapid.query)}
                            >
                                Ask a follow-up
                            </BTButton>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
